// API client for the label service
const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

// Admin API calls go through the SWA linked backend proxy (/api/v1/...)
// so SWA automatically injects x-ms-client-principal for staff auth.
const ADMIN_API_BASE = '/api/v1';

// API key storage
let currentApiKey: string | null = null;

export function setApiKey(apiKey: string | null) {
  currentApiKey = apiKey;
  if (apiKey) {
    localStorage.setItem('edi-label-api-key', apiKey);
  } else {
    localStorage.removeItem('edi-label-api-key');
  }
}

export function getApiKey(): string | null {
  if (!currentApiKey) {
    currentApiKey = localStorage.getItem('edi-label-api-key');
  }
  return currentApiKey;
}

export function clearApiKey() {
  currentApiKey = null;
  localStorage.removeItem('edi-label-api-key');
}

async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const apiKey = getApiKey();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }

  // When using API key auth, call the Function App directly (API_BASE).
  // When no API key (staff AAD auth), route through SWA proxy so it
  // injects x-ms-client-principal automatically.
  const baseUrl = apiKey ? API_BASE : ADMIN_API_BASE;
  
  const response = await fetch(`${baseUrl}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      // Clear invalid API key
      clearApiKey();
      throw new Error('Invalid or expired API key. Please log in again.');
    }
    const error = await response.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(error.error?.message || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

/**
 * Fetch for admin endpoints — routes through SWA linked backend proxy.
 * SWA injects x-ms-client-principal automatically on proxied requests.
 */
async function fetchAdminApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  
  const response = await fetch(`${ADMIN_API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new Error('Staff authentication required. Please log in with your Microsoft account.');
    }
    const error = await response.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(error.error?.message || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

// Types
export interface Template {
  templateKey: string;
  templateId?: string; // For admin template management
  industry?: string;
  company?: string;
  templateName?: string;
  retailer: string;
  labelType: string;
  version: string;
  status: string;
  description?: string;
  name?: string;
  width?: number;
  height?: number;
  dpi?: number;
  zplContent?: string;
  fields: Array<{
    name: string;
    type: string;
    required: boolean;
    description?: string;
  }>;
  examplePayload?: {
    templateKey?: string;
    data: Record<string, unknown>;
  };
  createdAt: string;
  updatedAt: string;
}

export interface UsageSummary {
  period: { start: string; end: string };
  totalLabels: number;
  successfulLabels: number;
  failedLabels: number;
  uniqueTemplates: number;
  avgLatencyMs: number;
  byTemplate: Array<{
    templateKey: string;
    retailer: string;
    labelType: string;
    count: number;
    avgLatencyMs: number;
  }>;
  byDay: Array<{
    date: string;
    count: number;
  }>;
}

export interface GenerateLabelRequest {
  templateKey?: string;
  retailer?: string;
  company?: string;
  industry?: string;
  labelType?: string;
  data: Record<string, unknown>;
}

export interface GenerateLabelResponse {
  zpl: string;
  templateKey: string;
  templateVersion: string;
  requestId: string;
  warnings: string[];
}

/** Matches GET /labels/layout-map response from the label API */
export interface LabelLayoutZone {
  id: string;
  label: string;
  xNorm: number;
  yNorm: number;
  wNorm: number;
  hNorm: number;
  contentType?: string;
  dataFields?: string[];
}

export interface LabelLayoutField {
  name?: string;
  xNorm: number;
  yNorm: number;
  xDots?: number;
  yDots?: number;
  line?: number;
}

export interface LabelLayoutMap {
  templateKey: string;
  widthDots: number;
  heightDots: number;
  dpi: number;
  widthIn?: number;
  heightIn?: number;
  zoneSource?:
    | 'stored'
    | 'onboarding-zones'
    | 'zpl-derived'
    | 'gs1-4x6'
    | 'grid-3x3'
    | 'manual';
  zones: LabelLayoutZone[];
  fields: LabelLayoutField[];
}

export type IssueCategory =
  | 'position'
  | 'size'
  | 'missing'
  | 'wrong_content'
  | 'barcode'
  | 'spacing'
  | 'font'
  | 'other';

export type AnnotationTargetType = 'region' | 'zone' | 'field';

export interface Annotation {
  /** Client-side id for list management (not required by API). */
  id: string;
  targetType: AnnotationTargetType;
  zoneId?: string;
  fieldName?: string;
  /** Normalized 0–1 coordinates relative to the label image. */
  xNorm: number;
  yNorm: number;
  wNorm?: number;
  hNorm?: number;
  category: IssueCategory;
  comment: string;
}

/** Wire shape for POST /labels/feedback annotations */
export type LabelFeedbackAnnotationPayload = {
  xNorm: number;
  yNorm: number;
  wNorm?: number;
  hNorm?: number;
  targetType?: AnnotationTargetType;
  zoneId?: string;
  fieldName?: string;
  issueCategory?: IssueCategory;
  comment?: string;
};

export type FeedbackSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface LabelFeedbackSubmit {
  templateKey: string;
  templateVersion?: string;
  source: 'portal';
  overallComment?: string;
  severity?: FeedbackSeverity;
  payloadSample?: Record<string, unknown>;
  annotations?: LabelFeedbackAnnotationPayload[];
}

export interface LabelFeedbackSubmitResponse {
  feedbackId: string;
  createdAt: string;
  /** True when ops escalation succeeded (issue and/or Teams/email). */
  escalated: boolean;
  /** Staff only — private GitHub issue URL (never returned to API-key clients). */
  githubIssueUrl?: string;
  githubIssueNumber?: number;
  /** Staff only — notify diagnostics */
  notifyStatus?: string;
  notifyError?: string;
}

export interface ChatToolCall {
  toolName: string;
  args: Record<string, unknown>;
  result: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  toolCalls?: ChatToolCall[];
  name?: string;
}

export interface ChatResponse {
  conversationId: string;
  reply: string;
  toolCalls: ChatToolCall[];
}

export interface ConversationSummary {
  conversationId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export type ChatStreamEvent =
  | { type: 'delta'; content: string }
  | { type: 'tool_start'; toolName: string; args: Record<string, unknown> }
  | { type: 'tool_result'; toolName: string; result: string; success: boolean }
  | { type: 'done'; conversationId: string; reply: string; toolCalls: ChatToolCall[] }
  | { type: 'error'; message: string };

export interface Tenant {
  tenantId: string;
  name: string;
  email: string;
  status: string;
  pricingPlanId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiKey {
  keyId: string;
  keyPrefix: string;
  name: string;
  status: string;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
  apiKey?: string; // Only on creation
}

export interface PricingPlan {
  planId: string;
  name: string;
  description: string;
  planType: 'per-label' | 'flat-rate';
  perLabelRate: number; // cents
  perTemplateMonthlyFee: number; // cents
  monthlyFlatRate: number; // cents (used when planType = 'flat-rate')
  includedLabels: number;
  includedTemplates: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SystemHealth {
  status: string;
  timestamp: string;
  version: string;
  checks: Record<string, { status: string; latencyMs?: number; error?: string }>;
  storage?: { status: string; latency: number };
  functions?: { status: string; latency: number };
  uptime?: number;
}

export interface DashboardSummary {
  tenants: {
    total: number;
    active: number;
    suspended: number;
    pending: number;
  };
  templates: {
    total: number;
    active: number;
    draft: number;
    approved: number;
  };
  usage: {
    thisMonth: {
      totalLabels: number;
      successCount: number;
      errorCount: number;
      estimatedRevenue: number;
    };
    trend: Array<{
      date: string;
      labels: number;
      errors: number;
    }>;
  };
  health: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    storageLatencyMs: number;
    apiLatencyMs: number;
  };
  recentActivity: Array<{
    timestamp: string;
    type: 'label_generated' | 'tenant_created' | 'template_uploaded';
    details: string;
    tenantId?: string;
    tenantName?: string;
  }>;
}

export interface UsageMetrics {
  timeRange: string;
  totalLabels: number;
  successRate: number;
  avgLatencyMs: number;
  byHour: Array<{ hour: string; count: number }>;
  byTenant: Array<{ tenantId: string; count: number }>;
  timeSeries?: Array<{ time: string; labels: number; errors: number; latency: number }>;
  recentErrors?: Array<{ timestamp: string; message: string; tenantId?: string }>;
}

export interface MonitoringMetrics {
  period: { days: number; start: string; end: string };
  totals: { labels: number; errors: number };
  timeSeries: Array<{ time: string; labels: number; errors: number; latency: number }>;
  tenantDistribution: Array<{ name: string; value: number }>;
  labelTypeDistribution: Array<{ name: string; value: number }>;
  tenantActivity: Array<{
    tenantId: string;
    tenantName: string;
    labelsToday: number;
    errorsToday: number;
    avgLatencyMs: number;
  }>;
}

export interface LabelaryMonitoringSnapshot {
  period: { today: string; start: string; end: string; days: number };
  limits: { daily: number; perSecond: number; tier: 'free' | 'plus' | 'business' };
  today: {
    successfulCalls: number;
    upstream429Count: number;
    internalLimitCount: number;
    totalTrackedCalls: number;
    usagePercent: number;
    remainingCalls: number;
    lastUpstream429At?: string;
    lastAlertEmailAt?: string;
  };
  history: Array<{
    date: string;
    successfulCalls: number;
    upstream429Count: number;
    internalLimitCount: number;
    usagePercent: number;
  }>;
  live: { callsInLastSecond: number; circuitState: string };
  upgrade: {
    status: 'healthy' | 'warning' | 'critical';
    recommendedTier: 'free' | 'plus' | 'business';
    usagePercent: number;
    message: string;
  };
}

export interface BillingSummary {
  month: string;
  totalRevenue: number;
  totalLabels: number;
  tenantCount: number;
  byTenant: Array<{
    tenantId: string;
    tenantName: string;
    labels: number;
    charge: number;
  }>;
  // Azure cost & margin fields (null if no cost data entered)
  azureCosts: {
    dev: AzureCostBreakdown | null;
    prod: AzureCostBreakdown | null;
  } | null;
  totalAzureCost: number | null;
  margin?: number | null;
  marginPercent?: number | null;
}

export interface AzureCostBreakdown {
  functionsCost: number;
  storageCost: number;
  appInsightsCost: number;
  staticWebAppCost: number;
  otherCost: number;
  totalCost: number;
  notes?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface AzureCostSummary {
  month: string;
  environments: {
    dev: AzureCostBreakdown | null;
    prod: AzureCostBreakdown | null;
  };
  combinedTotal: number;
}

export interface BillingReport {
  tenantId: string;
  tenantName: string;
  period: string;
  totalLabels: number;
  includedLabels?: number;
  billableLabels?: number;
  baseCharge?: number;
  labelCharges?: number;
  templateCharges?: number;
  totalCharge?: number;
  totalAmount: number;
  lineItems: Array<{
    templateKey: string;
    labelType: string;
    labelCount: number;
    rate: number;
    amount: number;
  }>;
}

// Retailer types
export type RetailerIndustry = 
  | 'retail'
  | 'grocery'
  | 'automotive'
  | 'electronics'
  | 'apparel'
  | 'home-goods'
  | 'convenience'
  | 'travel-center'
  | 'warehouse'
  | 'other';

export interface Retailer {
  retailerId: string;
  name: string;
  industry: RetailerIndustry;
  status: 'active' | 'inactive';
  ediPartnerId?: string;
  website?: string;
  contactEmail?: string;
  notes?: string;
  templateCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateAssignment {
  templateKey: string;
  assignedAt: string;
  assignedBy: string;
  templateName: string;
  labelType: string;
  version: string;
  status: string;
}

export interface Industry {
  id: RetailerIndustry;
  name: string;
  description: string;
}

export type RetailerLabelType =
  | 'shipping'
  | 'pallet'
  | 'carton'
  | 'box'
  | 'innerpack'
  | 'placard'
  | 'custom';

export type RetailerSeverityLevel = 'high' | 'medium' | 'low';

export type RetailerFieldType = 'string' | 'number' | 'date' | 'barcode';

export interface RetailerCommonMistake {
  mistake: string;
  correction: string;
  severity: RetailerSeverityLevel;
}

export interface RetailerContact {
  name: string;
  role: string;
  email: string;
}

export interface RetailerTemplateDimensions {
  width: number;
  height: number;
  unit: string;
  dpi: number;
}

export interface RetailerTemplateSummary {
  templateKey: string;
  displayName?: string;
  labelType?: RetailerLabelType;
  dimensions?: RetailerTemplateDimensions;
}

export interface RetailerManifest {
  retailerId: string;
  displayName: string;
  supportedPrograms: string[];
  labelTypes: RetailerLabelType[];
  activeTemplates: string[];
  contentVersion: string;
  lastReviewedAt: string;
  commonMistakes?: RetailerCommonMistake[];
  contacts?: RetailerContact[];
  specificationUrl?: string;
  notes?: string;
  templates?: RetailerTemplateSummary[];
}

export interface RetailerFieldDefinition {
  name: string;
  type: RetailerFieldType;
  description: string;
  maxLength?: number;
  validation?: string;
}

export interface RetailerTemplateDoc {
  templateKey: string;
  displayName: string;
  labelType: RetailerLabelType;
  dimensions: RetailerTemplateDimensions;
  purpose: string;
  whenToUse: string;
  whenNotToUse?: string;
  requiredFields?: RetailerFieldDefinition[];
  optionalFields?: RetailerFieldDefinition[];
  examplePayload?: Record<string, unknown>;
  specReference?: string;
  previewImage?: string;
}

// Onboarding types

export type OnboardingStatus =
  | 'queued' | 'sourcing' | 'extracting' | 'review_specs'
  | 'composing' | 'review_labels' | 'provisioning'
  | 'completed' | 'failed' | 'cancelled';

export const ONBOARDING_STATUSES: OnboardingStatus[] = [
  'queued', 'sourcing', 'extracting', 'review_specs',
  'composing', 'review_labels', 'provisioning',
  'completed', 'failed', 'cancelled',
];

/** Pipeline stages in order (for the stepper) */
export const PIPELINE_STAGES: OnboardingStatus[] = [
  'queued', 'sourcing', 'extracting', 'review_specs',
  'composing', 'review_labels', 'provisioning', 'completed',
];

export interface FieldRequirement {
  fieldPath: string;
  label: string;
  format?: string;
  position?: string;
}

export interface BarcodeRequirement {
  name: string;
  symbology: string;
  applicationIdentifier?: string;
  length?: number;
  description: string;
}

export interface LabelTypeRequirement {
  labelType: 'shipping' | 'carton' | 'pallet' | 'box' | 'placard' | 'custom';
  name: string;
  dimensions: { width: number; height: number; unit: string };
  dpi: number;
  requiredFields: FieldRequirement[];
  optionalFields: FieldRequirement[];
  barcodes: BarcodeRequirement[];
  layoutNotes: string;
  sampleImageUrl?: string;
}

export interface LabelRequirements {
  labelTypes: LabelTypeRequirement[];
  generalCompliance: {
    manualTitle?: string;
    manualVersion?: string;
    specialRequirements: string[];
    penalties?: string;
  };
}

export interface GeneratedTemplate {
  templateKey: string;
  templateName: string;
  labelType: string;
  zplContent: string;
  partialsUsed: string[];
  partialGaps: string[];
  validationResult: {
    staticValid: boolean;
    staticErrors: string[];
    previewBlobPath?: string;
  };
  requiredFields: string[];
  optionalFields: string[];
  dimensions: { width: number; height: number; unit: string };
  dpi: number;
}

export interface SpecSourceResult {
  url: string;
  confidence: number;
  title: string;
  source: string;
}

export interface OnboardingJobSummary {
  jobId: string;
  partnerName: string;
  partnerSlug: string;
  industryHint?: string;
  status: OnboardingStatus;
  currentStage: string;
  totalTokensUsed: number;
  estimatedCost: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  failedReason?: string;
}

export interface OnboardingJobDetail {
  jobId: string;
  partnerName: string;
  partnerSlug: string;
  industryHint?: string;
  status: OnboardingStatus;
  currentStage: string;
  specUrls: SpecSourceResult[];
  specBlobPaths: string[];
  extractedRequirements: LabelRequirements | null;
  extractionConfidence: number | null;
  generatedTemplates: GeneratedTemplate[];
  generatedConfig: Record<string, unknown> | null;
  provisionedRetailerId: string | null;
  provisionedTemplateIds: string[];
  provisionedAssignmentIds: string[];
  sourcingCompletedAt: string | null;
  extractionCompletedAt: string | null;
  compositionCompletedAt: string | null;
  provisioningCompletedAt: string | null;
  specsReviewedBy: string | null;
  specsReviewedAt: string | null;
  specsReviewNotes: string | null;
  labelsReviewedBy: string | null;
  labelsReviewedAt: string | null;
  labelsReviewNotes: string | null;
  totalTokensUsed: number;
  totalApiCalls: number;
  estimatedCost: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  failedAt: string | null;
  failedStage: string | null;
  failedReason: string | null;
  auditLog: AuditEntry[];
}

export interface OnboardingStats {
  byStatus: Record<string, number>;
  total: number;
}

export interface CreateOnboardingJobInput {
  partnerName: string;
  industryHint?: RetailerIndustry;
  specUrls?: string[];
  createdBy: string;
}

export interface BatchCreateOnboardingInput {
  partners: Array<{
    name: string;
    industryHint?: RetailerIndustry;
    specUrls?: string[];
  }>;
  createdBy: string;
}

export interface OnboardingReviewInput {
  action: 'approve' | 'reject';
  notes?: string;
  editedRequirements?: LabelRequirements;
  reviewer: string;
}

export interface AuditEntry {
  timestamp: string;
  stage: string;
  action: string;
  actor: string;
  details: string;
  metadata?: Record<string, unknown>;
}

export interface PartialGapAggregate {
  requirement: string;
  description: string;
  occurrences: number;
  affectedPartners: string[];
  suggestedApproach: string;
}

export interface QueueProcessResult {
  processed: number;
  skipped: number;
  errors: string[];
}

export interface OnboardingDiagnostics {
  agents: Record<string, boolean>;
  envVars: Record<string, boolean>;
  optionalVars?: Record<string, boolean>;
  info?: Record<string, string>;
  circuitBreaker: { isOpen: boolean } | null;
  pipelineReady: boolean;
}

// Client API
export const clientApi = {
  getTemplates: () => fetchApi<Template[]>('/templates'),

  getRetailers: () => fetchApi<RetailerManifest[]>('/retailers'),

  getRetailerManifest: (retailerId: string) =>
    fetchApi<RetailerManifest>(`/retailers/${encodeURIComponent(retailerId)}`),

  getRetailerTemplateDoc: (retailerId: string, templateKey: string) =>
    fetchApi<RetailerTemplateDoc>(
      `/retailers/${encodeURIComponent(retailerId)}/templates/${encodeURIComponent(templateKey)}`,
    ),
  
  getUsage: (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    return fetchApi<UsageSummary>(`/usage?${params}`);
  },
  
  generateLabel: (request: GenerateLabelRequest, tenantId?: string) =>
    fetchApi<GenerateLabelResponse>('/labels/zpl', {
      method: 'POST',
      headers: tenantId ? { 'X-Tenant-Id': tenantId } : undefined,
      body: JSON.stringify(request),
    }),
  
  generatePreview: async (request: GenerateLabelRequest & { format?: 'png' | 'pdf'; dpi?: number }, tenantId?: string): Promise<Blob> => {
    const apiKey = getApiKey();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }
    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId;
    }
    
    const baseUrl = apiKey ? API_BASE : ADMIN_API_BASE;
    
    const response = await fetch(`${baseUrl}/labels/preview`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Request failed' } }));
      throw new Error(error.error?.message || `HTTP ${response.status}`);
    }
    
    return response.blob();
  },

  getLabelLayoutMap: (templateKey: string, tenantId?: string) =>
    fetchApi<LabelLayoutMap>(
      `/labels/layout-map?templateKey=${encodeURIComponent(templateKey)}`,
      {
        headers: tenantId ? { 'X-Tenant-Id': tenantId } : undefined,
      },
    ),

  submitLabelFeedback: (body: LabelFeedbackSubmit, tenantId?: string) =>
    fetchApi<LabelFeedbackSubmitResponse>('/labels/feedback', {
      method: 'POST',
      headers: tenantId ? { 'X-Tenant-Id': tenantId } : undefined,
      body: JSON.stringify(body),
    }),

  sendChatMessage: (messages: ChatMessage[], conversationId?: string) =>
    fetchApi<ChatResponse>('/chat', {
      method: 'POST',
      body: JSON.stringify({
        ...(conversationId ? { conversationId } : {}),
        messages: messages.map(({ role, content, name }) => ({
          role,
          content,
          ...(name ? { name } : {}),
        })),
      }),
    }),

  listConversations: (limit = 20) =>
    fetchApi<{ conversations: ConversationSummary[] }>(`/chat/conversations?limit=${limit}`),

  getConversation: (conversationId: string) =>
    fetchApi<{ conversationId: string; messages: ChatMessage[] }>(`/chat/conversations/${encodeURIComponent(conversationId)}`),


  streamChatMessage: async (
    messages: ChatMessage[],
    onEvent: (event: ChatStreamEvent) => void,
    signal?: AbortSignal,
    conversationId?: string,
  ): Promise<void> => {
    const apiKey = getApiKey();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    };
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }
    const baseUrl = apiKey ? API_BASE : ADMIN_API_BASE;

    const response = await fetch(`${baseUrl}/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...(conversationId ? { conversationId } : {}),
        messages: messages.map(({ role, content, name }) => ({
          role,
          content,
          ...(name ? { name } : {}),
        })),
      }),
      signal,
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearApiKey();
        throw new Error('Invalid or expired API key. Please log in again.');
      }
      const error = await response.json().catch(() => ({ error: { message: 'Request failed' } }));
      throw new Error(error.error?.message || `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const parsed = JSON.parse(line.slice(6)) as ChatStreamEvent;
              onEvent(parsed);
            } catch { /* skip malformed events */ }
            eventType = '';
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },
};

// Admin API — routes through SWA linked backend proxy for automatic staff auth
export const adminApi = {
  // Dashboard
  getDashboardSummary: () => fetchAdminApi<DashboardSummary>('/ops/dashboard/summary'),

  // Tenants
  getTenants: () => fetchAdminApi<Tenant[]>('/ops/tenants'),
  
  getTenant: (tenantId: string) => fetchAdminApi<Tenant>(`/ops/tenants/${tenantId}`),
  
  createTenant: (data: { name: string; email: string; pricingPlanId: string }) =>
    fetchAdminApi<Tenant>('/ops/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateTenant: (tenantId: string, data: Partial<Tenant>) =>
    fetchAdminApi<Tenant>(`/ops/tenants/${tenantId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  // API Keys
  getApiKeys: (tenantId: string) => fetchAdminApi<ApiKey[]>(`/ops/tenants/${tenantId}/apikeys`),
  
  createApiKey: (tenantId: string, data: { name: string; expiresAt?: string }) =>
    fetchAdminApi<ApiKey>(`/ops/tenants/${tenantId}/apikeys`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  revokeApiKey: (tenantId: string, keyId: string) =>
    fetchAdminApi<void>(`/ops/tenants/${tenantId}/apikeys/${keyId}`, {
      method: 'DELETE',
    }),

  rotateApiKey: (tenantId: string, keyId: string) =>
    fetchAdminApi<{ newKey: ApiKey; deprecatedKey: ApiKey }>(`/ops/tenants/${tenantId}/apikeys/${keyId}/rotate`, {
      method: 'POST',
    }),

  // Tenant Templates
  getTenantTemplates: (tenantId: string) =>
    fetchAdminApi<TemplateAssignment[]>(`/ops/tenants/${tenantId}/templates`),

  unassignTemplate: (templateKey: string, tenantId: string) =>
    fetchAdminApi<void>(`/ops/templates/${templateKey}/assign/${tenantId}`, {
      method: 'DELETE',
    }),

  bulkAssignTemplates: (tenantId: string, templateKeys: string[]) =>
    fetchAdminApi<{ success: boolean; tenantId: string; assigned: number; templateKeys: string[] }>(
      `/ops/tenants/${tenantId}/templates/bulk-assign`,
      {
        method: 'POST',
        body: JSON.stringify({ templateKeys }),
      }
    ),

  // Templates
  getTemplates: (options?: { tenantId?: string; industry?: string; company?: string; labelType?: string; search?: string }) => {
    const params = new URLSearchParams();
    if (options?.tenantId && options.tenantId !== '_global') params.set('tenantId', options.tenantId);
    if (options?.industry) params.set('industry', options.industry);
    if (options?.company) params.set('company', options.company);
    if (options?.labelType) params.set('labelType', options.labelType);
    if (options?.search) params.set('search', options.search);
    const queryString = params.toString();
    return fetchAdminApi<Template[]>(`/ops/templates${queryString ? `?${queryString}` : ''}`);
  },
  
  getTemplate: (templateKey: string) =>
    fetchAdminApi<{ templateKey: string; versions: Template[]; activeVersion: string; zplContent?: string }>(
      `/ops/templates/${templateKey}`
    ),
  
  createTemplate: (data: {
    templateKey?: string;
    tenantId?: string;
    industry: string;
    company: string;
    templateName?: string;
    labelType: string;
    version?: string;
    description?: string;
    fields?: Array<{ name: string; type: string; required: boolean; description?: string }>;
    zplContent: string;
    width?: number;
    height?: number;
    dpi?: number;
  }) =>
    fetchAdminApi<Template>('/ops/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  approveTemplate: (templateKey: string, version: string) =>
    fetchAdminApi<{ success: boolean }>(`/ops/templates/${templateKey}/approve?version=${version}`, {
      method: 'POST',
    }),
  
  activateTemplate: (templateKey: string, version: string) =>
    fetchAdminApi<{ success: boolean }>(`/ops/templates/${templateKey}/activate?version=${version}`, {
      method: 'POST',
    }),
  
  assignTemplate: (templateKey: string, tenantIdOrData: string | { templateId: string; labelType: string; priority: number }) => {
    if (typeof tenantIdOrData === 'string') {
      return fetchAdminApi<{ success: boolean }>(`/ops/templates/${templateKey}/assign`, {
        method: 'POST',
        body: JSON.stringify({ tenantId: tenantIdOrData }),
      });
    }
    return fetchAdminApi<{ success: boolean }>(`/ops/templates/${templateKey}/assign`, {
      method: 'POST',
      body: JSON.stringify(tenantIdOrData),
    });
  },
  
  // Billing
  getPricingPlans: () => fetchAdminApi<PricingPlan[]>('/ops/pricing-plans'),

  createPricingPlan: (data: {
    name: string;
    description?: string;
    planType: 'per-label' | 'flat-rate';
    perLabelRate: number;
    perTemplateMonthlyFee: number;
    monthlyFlatRate: number;
    includedLabels: number;
    includedTemplates: number;
  }) =>
    fetchAdminApi<PricingPlan>('/ops/pricing-plans', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getBillingReport: (tenantId: string, month: string) =>
    fetchAdminApi<BillingReport>(`/ops/billing/${tenantId}?month=${month}`),

  getBillingExport: async (month: string, format: 'json' | 'csv' = 'json', tenantId?: string) => {
    const params = new URLSearchParams({ month, format });
    if (tenantId) params.set('tenantId', tenantId);
    
    if (format === 'csv') {
      const response = await fetch(`${ADMIN_API_BASE}/ops/billing/export?${params}`);
      return response.text();
    }
    return fetchAdminApi<unknown[]>(`/ops/billing/export?${params}`);
  },
  
  getTenantBilling: (tenantId: string, month?: string) => {
    const params = month ? `?month=${month}` : '';
    return fetchAdminApi<{
      tenantId: string;
      tenantName: string;
      period: string;
      totalLabels: number;
      totalAmount: number;
      lineItems: Array<{
        templateKey: string;
        labelType: string;
        labelCount: number;
        rate: number;
        amount: number;
      }>;
    }>(`/ops/billing/${tenantId}${params}`);
  },
  
  // Monitoring
  getHealth: () => fetchAdminApi<SystemHealth>('/ops/monitoring/health'),

  getSystemHealth: () => fetchAdminApi<SystemHealth>('/ops/monitoring/health'),

  getUsageMetrics: (timeRange: string) =>
    fetchAdminApi<UsageMetrics>(`/ops/monitoring/usage?timeRange=${timeRange}`),

  getMonitoringMetrics: (days: number = 7) =>
    fetchAdminApi<MonitoringMetrics>(`/ops/monitoring/metrics?days=${days}`),

  getLabelaryMonitoring: (days: number = 7) =>
    fetchAdminApi<LabelaryMonitoringSnapshot>(`/ops/monitoring/labelary?days=${days}`),

  /**
   * Side-by-side ZPL renderer comparison (Labelary + bake-off candidates).
   * Staff-only; do not log image payloads.
   */
  compareRenderers: (body: {
    zpl?: string;
    templateKey?: string;
    data?: Record<string, unknown>;
    tenantId?: string;
    dpi?: number;
    width?: number;
    height?: number;
    providers?: Array<'labelary' | 'stand-in' | 'binarykits' | 'zpl-js'>;
  }) =>
    fetchAdminApi<{
      zplHash: string;
      zplPreview: string;
      labelCount: number;
      providers: Array<{
        id: string;
        name: string;
        role: string;
        ok: boolean;
        latencyMs: number;
        contentType?: string;
        imageBase64?: string;
        outputHash?: string;
        pages?: number;
        error?: string;
        unsupported?: boolean;
        notes?: string[];
        available: boolean;
      }>;
      comparedAt: string;
    }>('/ops/renderer-compare', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getBillingSummary: (month?: string) => {
    const params = month ? `?month=${month}` : '';
    return fetchAdminApi<BillingSummary>(`/ops/monitoring/billing-summary${params}`);
  },

  // Azure Costs
  getAzureCosts: (month: string) =>
    fetchAdminApi<AzureCostSummary>(`/ops/azure-costs?month=${month}`),

  updateAzureCost: (data: {
    month: string;
    environment: 'dev' | 'prod';
    functionsCost: number;
    storageCost: number;
    appInsightsCost: number;
    staticWebAppCost: number;
    otherCost: number;
    notes?: string;
  }) =>
    fetchAdminApi<AzureCostBreakdown>('/ops/azure-costs', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getUsageAnalytics: (startDate?: string, endDate?: string, tenantId?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (tenantId) params.set('tenantId', tenantId);
    return fetchAdminApi<unknown>(`/ops/monitoring/usage?${params}`);
  },

  // Retailers
  getRetailers: (options?: { industry?: RetailerIndustry; status?: 'active' | 'inactive'; search?: string }) => {
    const params = new URLSearchParams();
    if (options?.industry) params.set('industry', options.industry);
    if (options?.status) params.set('status', options.status);
    if (options?.search) params.set('search', options.search);
    const queryString = params.toString();
    return fetchAdminApi<{ retailers: Retailer[]; total: number }>(`/ops/retailers${queryString ? `?${queryString}` : ''}`);
  },

  getRetailer: (retailerId: string) => fetchAdminApi<Retailer>(`/ops/retailers/${retailerId}`),

  createRetailer: (data: {
    retailerId: string;
    name: string;
    industry: RetailerIndustry;
    ediPartnerId?: string;
    website?: string;
    contactEmail?: string;
    notes?: string;
  }) =>
    fetchAdminApi<Retailer>('/ops/retailers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRetailer: (retailerId: string, data: {
    name?: string;
    industry?: RetailerIndustry;
    status?: 'active' | 'inactive';
    ediPartnerId?: string;
    website?: string;
    contactEmail?: string;
    notes?: string;
  }) =>
    fetchAdminApi<Retailer>(`/ops/retailers/${retailerId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteRetailer: (retailerId: string) =>
    fetchAdminApi<void>(`/ops/retailers/${retailerId}`, {
      method: 'DELETE',
    }),

  getIndustries: () => fetchAdminApi<{ industries: Industry[] }>('/ops/industries'),

  // Onboarding
  onboarding: {
    list: async (filters?: { status?: string }): Promise<OnboardingJobSummary[]> => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      const qs = params.toString();
      const result = await fetchAdminApi<{ jobs: OnboardingJobSummary[]; total: number }>(`/ops/onboarding${qs ? `?${qs}` : ''}`);
      return result.jobs ?? [];
    },

    get: (jobId: string) =>
      fetchAdminApi<OnboardingJobDetail>(`/ops/onboarding/${jobId}`),

    create: (data: CreateOnboardingJobInput) =>
      fetchAdminApi<OnboardingJobSummary>('/ops/onboarding', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    createBatch: (data: BatchCreateOnboardingInput) =>
      fetchAdminApi<OnboardingJobSummary[]>('/ops/onboarding/batch', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (jobId: string, data: Record<string, unknown>) =>
      fetchAdminApi<void>(`/ops/onboarding/${jobId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (jobId: string) =>
      fetchAdminApi<void>(`/ops/onboarding/${jobId}`, {
        method: 'DELETE',
      }),

    advance: (jobId: string) =>
      fetchAdminApi<void>(`/ops/onboarding/${jobId}/advance`, { method: 'POST' }),

    retry: (jobId: string) =>
      fetchAdminApi<void>(`/ops/onboarding/${jobId}/retry`, { method: 'POST' }),

    reviewSpecs: (jobId: string, data: OnboardingReviewInput) =>
      fetchAdminApi<void>(`/ops/onboarding/${jobId}/review-specs`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    reviewLabels: (jobId: string, data: OnboardingReviewInput) =>
      fetchAdminApi<void>(`/ops/onboarding/${jobId}/review-labels`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    stats: () =>
      fetchAdminApi<OnboardingStats>('/ops/onboarding/stats'),

    partialGaps: () =>
      fetchAdminApi<{ gaps: PartialGapAggregate[]; total: number }>('/ops/onboarding/partial-gaps'),

    processQueue: (options?: { maxJobs?: number; delayMs?: number }) =>
      fetchAdminApi<QueueProcessResult>('/ops/onboarding/process-queue', {
        method: 'POST',
        body: JSON.stringify(options ?? {}),
      }),

    diagnostics: () =>
      fetchAdminApi<OnboardingDiagnostics>('/ops/onboarding/diagnostics'),

    uploadSpec: async (jobId: string, file: File): Promise<{ jobId: string; filename: string; blobPath: string; sizeBytes: number; message: string }> => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${ADMIN_API_BASE}/ops/onboarding/${jobId}/upload-spec`, {
        method: 'POST',
        body: formData,
        // Do NOT set Content-Type — browser sets it with correct multipart boundary
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Staff authentication required. Please log in with your Microsoft account.');
        }
        const error = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
        throw new Error(error.error?.message || `HTTP ${response.status}`);
      }

      return response.json();
    },
  },

  // Label appearance feedback inbox
  listLabelFeedback: (filters?: {
    tenantId?: string;
    status?: 'new' | 'reviewed' | 'resolved';
    severity?: string;
    source?: string;
    templateKey?: string;
    hasGithubIssue?: boolean;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.tenantId) params.set('tenantId', filters.tenantId);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.severity) params.set('severity', filters.severity);
    if (filters?.source) params.set('source', filters.source);
    if (filters?.templateKey) params.set('templateKey', filters.templateKey);
    if (filters?.hasGithubIssue === true) params.set('hasGithubIssue', 'true');
    if (filters?.hasGithubIssue === false) params.set('hasGithubIssue', 'false');
    if (filters?.limit != null) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return fetchAdminApi<LabelFeedbackListResponse>(
      `/ops/label-feedback${qs ? `?${qs}` : ''}`,
    );
  },

  updateLabelFeedbackStatus: (
    tenantId: string,
    feedbackId: string,
    status: 'new' | 'reviewed' | 'resolved',
  ) =>
    fetchAdminApi<LabelFeedbackListItem>(
      `/ops/label-feedback/${encodeURIComponent(tenantId)}/${encodeURIComponent(feedbackId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      },
    ),
};

export interface LabelFeedbackListItem {
  feedbackId: string;
  tenantId: string;
  templateKey: string;
  templateVersion?: string;
  source: string;
  overallComment?: string;
  severity?: string;
  status: string;
  createdAt: string;
  createdBy?: string;
  annotations: Array<Record<string, unknown>>;
  annotationCount?: number;
  zplHash?: string;
  githubIssueNumber?: number;
  githubIssueUrl?: string;
  notifyStatus?: string;
  notifyError?: string;
  notifiedAt?: string;
}

export interface LabelFeedbackListResponse {
  count: number;
  items: LabelFeedbackListItem[];
}
